import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Upload, CheckCircle2, XCircle, Save } from "lucide-react";
import Loader from "../../components/ui/Loader";
import RecruiterPageShell from "../../components/recruiter/RecruiterPageShell";
import { glassCard, glassPanel } from "../../utils/glassStyles";

export default function RecruiterProfileEdit() {
 const navigate = useNavigate();
 const [user, setUser] = useState(null);
 const [profile, setProfile] = useState({
 companyName: "",
 about: "",
 publicNote: "",
 location: "",
 website: "",
 logo: null,
 logoUrl: "",
 });

 const [logoName, setLogoName] = useState("");
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [message, setMessage] = useState("");
 const [error, setError] = useState("");

 // Check if user logged in via Telegram
 const isTelegramUser = user?.telegramId || window.Telegram?.WebApp?.initDataUnsafe?.user;

 // Fetch existing recruiter profile
 useEffect(() => {
 const fetchProfile = async () => {
 try {
 const [profileRes, userRes] = await Promise.all([
 api.get("/recruiter/profile"),
 api.get("/auth/me")
 ]);

 if (userRes.data && userRes.data.user) {
 setUser(userRes.data.user);
 }

 if (profileRes.data) {
 setProfile({
 companyName: profileRes.data.companyName || "",
 about: profileRes.data.about || "",
 publicNote: profileRes.data.publicNote || "",
 location: profileRes.data.location || "",
 website: profileRes.data.website || "",
 logo: null,
 logoUrl: profileRes.data.logoUrl || "",
 });
 }
 } catch (err) {
 console.error("Error fetching profile:", err);
 setError(err.response?.data?.message || "Failed to fetch profile.");
 } finally {
 setLoading(false);
 }
 };
 fetchProfile();
 }, []);

 // Handle field changes
 const handleChange = (e) => {
 const { name, value, files } = e.target;
 if (files && files[0]) {
 const file = files[0];
 const maxSize = 2 * 1024 * 1024; // 2MB in bytes
 
 if (file.size > maxSize) {
 setError(`File size exceeds 2MB limit. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB. Please choose a smaller file.`);
 e.target.value = '';
 return;
 }
 
 setProfile((prev) => ({ ...prev, logo: file }));
 setLogoName(file.name);
 setError('');
 } else {
 setProfile((prev) => ({ ...prev, [name]: value }));
 }
 };

 // Handle submit (update profile)
 const handleSubmit = async (e) => {
 e.preventDefault();
 setSaving(true);
 setError("");
 setMessage("");

 try {
 const formData = new FormData();
 formData.append("companyName", profile.companyName);
 formData.append("about", profile.about);
 formData.append("publicNote", profile.publicNote);
 formData.append("location", profile.location);
 formData.append("website", profile.website);

 if (profile.logo) {
 formData.append("logo", profile.logo);
 }

 const res = await api.patch("/recruiter/profile", formData, {
 headers: {
 "Content-Type": "multipart/form-data",
 },
 });
 
 setMessage("Profile updated successfully!");
 setProfile((prev) => ({
 ...prev,
 logoUrl: res.data.logoUrl || prev.logoUrl,
 }));
 
 setTimeout(() => navigate("/recruiter/profile"), 1500);
 } catch (err) {
 console.error("Error updating profile:", err);
 setError(err.response?.data?.message || "Failed to update profile.");
 } finally {
 setSaving(false);
 }
 };

 if (loading) return <Loader label="Loading profile..." />;

 return (
 <RecruiterPageShell
 title="Edit Profile"
 subtitle="Update your company information and public page content"
 onBack={() => navigate("/recruiter/profile")}
 backLabel="Back to Profile"
 >

 {message && (
 <Alert className="mb-6 border-green-500 bg-green-500/100/10 text-green-600">
 <CheckCircle2 className="h-4 w-4" />
 <AlertDescription>{message}</AlertDescription>
 </Alert>
 )}
 
 {error && (
 <Alert variant="destructive" className="mb-6">
 <XCircle className="h-4 w-4" />
 <AlertDescription>{error}</AlertDescription>
 </Alert>
 )}

 <Card className={`rounded-2xl ${glassPanel}`}>
 <CardHeader>
 <CardTitle>Company Details</CardTitle>
 </CardHeader>
 <CardContent>
 <form onSubmit={handleSubmit} className="space-y-6">
 <div className="space-y-2">
 <Label htmlFor="companyName">Company Name</Label>
 <Input
 id="companyName"
 name="companyName"
 value={profile.companyName}
 onChange={handleChange}
 placeholder="e.g. Tech Corp Inc"
 required
 />
 </div>

 <div className="space-y-2">
 <Label htmlFor="about">About Company</Label>
 <Textarea
 id="about"
 name="about"
 value={profile.about}
 onChange={handleChange}
 placeholder="Tell job seekers about your company, culture, and mission..."
 className="min-h-[100px]"
 />
 <p className="text-xs text-muted-foreground">Shown on your public company profile page.</p>
 </div>

 <div className="space-y-2">
 <Label htmlFor="publicNote">Public note (optional)</Label>
 <Textarea
 id="publicNote"
 name="publicNote"
 value={profile.publicNote}
 onChange={handleChange}
 placeholder="e.g. We review applications within 48 hours. Remote-friendly roles available."
 className="min-h-[80px]"
 />
 <p className="text-xs text-muted-foreground">
 Add a short note for applicants — hiring timeline, tips, or anything you want visible on your company page.
 </p>
 </div>

 <div className="space-y-2">
 <Label htmlFor="location">Location</Label>
 <Input
 id="location"
 name="location"
 value={profile.location}
 onChange={handleChange}
 placeholder="e.g. Addis Ababa, Ethiopia"
 />
 </div>

 <div className="space-y-2">
 <Label htmlFor="website">Website</Label>
 <Input
 id="website"
 name="website"
 type="url"
 value={profile.website}
 onChange={handleChange}
 placeholder="https://yourcompany.com"
 />
 </div>

 <div className="space-y-2">
 <Label htmlFor="logo">Company Logo</Label>
 <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
 <label
 htmlFor="logo"
 className="flex items-center justify-center px-4 py-2 border border-dashed border-input rounded-lg cursor-pointer hover:bg-accent transition"
 >
 <Upload className="w-5 h-5 mr-2" />
 <span className="font-medium">Choose File</span>
 </label>
 <input
 id="logo"
 type="file"
 name="logo"
 accept="image/*,.png,.jpg,.jpeg,.gif,.svg"
 className="hidden"
 onChange={handleChange}
 />
 {logoName && <p className="text-sm truncate max-w-[200px]">{logoName}</p>}
 </div>
 <p className="text-xs text-muted-foreground">
 Max size: 2MB. Supported: PNG, JPG, SVG
 </p>

 {profile.logoUrl && (
 <div className="mt-3">
 <img
 src={profile.logoUrl.startsWith('http') ? profile.logoUrl : `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/${profile.logoUrl}`}
 alt="Company Logo"
 className="h-20 w-20 object-contain border rounded-lg"
 />
 </div>
 )}
 </div>

 <div className="flex flex-col sm:flex-row gap-3 pt-4">
 <Button
 type="submit"
 disabled={saving}
 className="flex-1"
 >
 {saving ? (
 <>
 <Save className="w-4 h-4 mr-2 animate-spin" />
 Saving Changes...
 </>
 ) : (
 <>
 <Save className="w-4 h-4 mr-2" />
 Save Changes
 </>
 )}
 </Button>
 <Button
 type="button"
 variant="outline"
 onClick={() => navigate("/recruiter/profile")}
 disabled={saving}
 className="flex-1"
 >
 Cancel
 </Button>
 </div>
 </form>
 </CardContent>
 </Card>

 {/* Note about password for non-Telegram users */}
 {!isTelegramUser && (
 <Card className={`mt-6 rounded-2xl ${glassCard}`}>
 <CardHeader>
 <CardTitle>Password</CardTitle>
 </CardHeader>
 <CardContent>
 <p className="text-sm text-muted-foreground mb-4">
 To change your password, please contact support or use the password reset feature.
 </p>
 </CardContent>
 </Card>
 )}
 </RecruiterPageShell>
 );
}
